import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchUsers, createUser, toggleUserActive, updateUserStorage } from "../api/adminApi";
import { listClients } from "../api/clientApi";
import UserModal from "../components/UserModal";

function AdminUsersPage({ authHeaders, setToast }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState({}); // ✅ ADD
  const [formMessage, setFormMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredUsers = useMemo(() => {
    const q = String(searchQuery || "").trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      String(u.email || "").toLowerCase().includes(q)
      || String(u.fullName || "").toLowerCase().includes(q)
      || String(u.mobileNumber || "").toLowerCase().includes(q)
    );
  }, [users, searchQuery]);

  const loadUsers = useCallback(async () => {
    if (!authHeaders) return;
    setLoading(true);
    setError("");
    try {
      const records = await fetchUsers(authHeaders);
      setUsers(records);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!authHeaders) return;
      setClientsLoading(true);
      try {
        const list = await listClients(authHeaders);
        if (mounted) setClients(Array.isArray(list) ? list : []);
      } catch {
        if (mounted) setClients([]);
      } finally {
        if (mounted) setClientsLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [authHeaders]);

  const handleCreateFromModal = async (payload) => {
    if (!authHeaders) throw new Error("Missing authorization");
    setSaving(true);
    setFormMessage("");
    setGeneratedPassword(null);
    try {
      const { user, generatedPassword: autoPassword } = await createUser(payload, authHeaders);
      if (user) {
        // ✅ merge passwordPlain from response into user list
        const record = { ...user };
        setUsers((prev) => [record, ...prev.filter((e) => e?.id !== user.id)]);
        setToast?.("User created successfully");
      }
      if (autoPassword) setGeneratedPassword(autoPassword);
      return { user, generatedPassword: autoPassword };
    } catch (err) {
      const message = err.response?.data?.error
        || (err.response?.status === 400 ? 'Bad request' : err.message)
        || "Failed to create user";
      throw new Error(message);
    } finally {
      setSaving(false);
    }
  };

  if (!authHeaders) {
    return <div className="mail-empty">Authorization required to manage users.</div>;
  }

  return (
    <section className="admin-users">
      <header className="admin-users-header">
        <div className="admin-users-title">
          <h2>Users Management</h2>
        </div>
        <div className="admin-users-actions">
          <input
            type="search"
            placeholder="Search users by email, name or mobile"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="button" onClick={() => setIsModalOpen(true)}>
            Add New
          </button>
        </div>
      </header>

      <div className="admin-users-grid">
        <div className="admin-users-list" style={{ width: "100%" }}>
          {error && <div className="admin-users-error">{error}</div>}
          {loading && !users.length ? (
            <div className="mail-empty">Loading users…</div>
          ) : users.length === 0 ? (
            <div className="mail-empty">No users found.</div>
          ) : (
            <div className="admin-users-table-wrapper">
              <table className="admin-users-table">
                <thead>
                  <tr>
                    <th>Active</th>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Mobile</th>
                    <th>Gender</th>
                    <th>Company</th>
                    <th>Role</th>
                    <th>Storage</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id || user.email}>

                      {/* Active */}
                      <td>
                        <input
                          type="checkbox"
                          checked={user.isActive !== false}
                          onChange={async (e) => {
                            const next = !!e.target.checked;
                            try {
                              const updated = await toggleUserActive(user.id, next, authHeaders);
                              setUsers((prev) => prev.map((u) =>
                                u.id === user.id ? (updated || { ...u, isActive: next }) : u
                              ));
                              setToast?.(next ? "User activated" : "User deactivated");
                            } catch (err) {
                              setToast?.(err.response?.data?.error || err.message || "Failed to update user");
                            }
                          }}
                        />
                      </td>

                      <td>{user.email}</td>
                      <td>{user.fullName || "—"}</td>
                      <td>{user.mobileNumber || "—"}</td>
                      <td>{user.gender ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1) : "—"}</td>
                      <td>{clients.find((c) => c.id === user.clientId)?.name || "—"}</td>
                      <td>
                        {user.role === 1 ? "Superadmin" : user.role === 2 ? "Admin" : (user.isAdmin ? "Admin" : "User")}
                      </td>

                      {/* ✅ PASSWORD COLUMN */}
                      {/* <td>
                        {user.passwordPlain ? (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                              {visiblePasswords[user.id] ? user.passwordPlain : "•••••••"}
                            </span>
                            <button
                              type="button"
                              style={{ fontSize: 11, padding: '2px 8px' }}
                              onClick={() =>
                                setVisiblePasswords(p => ({ ...p, [user.id]: !p[user.id] }))
                              }
                            >
                              {visiblePasswords[user.id] ? "Hide" : "Show"}
                            </button>
                          </div>
                        ) : (
                          <span style={{ color: '#999' }}>—</span>
                        )}
                      </td> */}

                      {/* Storage */}
                      <td>
                        {(() => {
                          const used = user.usedStorageGB != null
                            ? String(user.usedStorageGB)
                            : (user.usedStorageBytes
                              ? (user.usedStorageBytes / (1024 * 1024 * 1024)).toFixed(2)
                              : "0.00");
                          return (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <span>{used} GB / {user.totalStorageGB} GB</span>
                              <button type="button" onClick={async () => {
                                const val = window.prompt(
                                  'Set total storage (GB) for ' + (user.email || ''),
                                  String(user.totalStorageGB || '')
                                );
                                if (val === null) return;
                                const parsed = parseFloat(val);
                                if (!Number.isFinite(parsed) || parsed < 0) {
                                  setToast?.('Invalid storage value'); return;
                                }
                                try {
                                  const updated = await updateUserStorage(user.id, parsed, authHeaders);
                                  if (updated) {
                                    setUsers((prev) => prev.map((u) => u.id === updated.id ? updated : u));
                                    setToast?.('Storage updated');
                                  }
                                } catch (err) {
                                  setToast?.(err.response?.data?.error || err.message || 'Failed to update storage');
                                }
                              }}>Edit</button>
                            </div>
                          );
                        })()}
                      </td>

                      <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <UserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreate={handleCreateFromModal}
        clients={clients}
        clientsLoading={clientsLoading}
      />
    </section>
  );
}

export default AdminUsersPage;